import { Drawer, TextField } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  CreateUserCompanyRoleAssignmentDto,
  CreateUserDto,
  UpdateUserDto,
  UsersListItemDto,
  UserRole,
} from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { AxiosError } from "axios";
import { isArray, isEmpty } from "lodash";
import { MuiTelInput } from "mui-tel-input";

import CompanyAssignmentTable from "./components/CompanyAssignmentTable";

import { useCreateUser, useUpdateUser, useGetCompaniesBasics } from "@api";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  CircularProgress,
} from "@components/base";
import { useUserStore } from "@store/user";

export type CompanyAssignmentFieldItem = CreateUserCompanyRoleAssignmentDto & {
  companyName: string;
  isAssigned: boolean;
};

export type FormValues = CreateUserDto &
  UpdateUserDto & {
    companies: CompanyAssignmentFieldItem[];
  };

interface UserEditor {
  open?: boolean;
  onClose: () => void;
  user?: UsersListItemDto;
}

const UserEditor: FC<UserEditor> = ({ open, onClose, user }) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "usersManagement.userEditor",
  });
  const { user: currentUser } = useUserStore();
  const { companiesBasics, companiesBasicsEnabled } =
    useGetCompaniesBasics(currentUser);
  const {
    createUserAsync,
    createUserEnabled,
    isPendingCreateUser,
    createUserError,
  } = useCreateUser(currentUser);
  const {
    updateUserAsync,
    updateUserEnabled,
    isPendingUpdateUser,
    updateUserError,
  } = useUpdateUser(currentUser);
  const [error, setError] = useState<string>();
  const isPending = isPendingCreateUser || isPendingUpdateUser;
  const editMode = !!user?.id;

  // TODO: implement a proper validation schema
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormValues>();

  const companies = watch("companies") as CompanyAssignmentFieldItem[];

  const getCompanyAssignmentFieldArray = (
    isNewUser?: boolean,
  ): CompanyAssignmentFieldItem[] => {
    const basics = companiesBasics?.items ?? [];

    if (user?.companies && !isNewUser) {
      return user.companies.map((company) => ({
        companyId: company.companyId,
        companyName: company.companyName,
        role: company.role ?? UserRole.Viewer,
        isAssigned: company.isAssigned ?? false,
      }));
    }

    return basics.map((company) => ({
      companyId: company.id,
      companyName: company.name,
      role: UserRole.Viewer,
      isAssigned: company.companyType === 0,
    }));
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (editMode) {
      await updateUserAsync({
        ...data,
        id: user.id,
        companies: data.companies,
      });
    } else {
      const assignedCompanies = data.companies
        ?.filter((company) => company.isAssigned)
        .map((company) => ({
          companyId: company.companyId,
          role: company.role,
          isAssigned: true,
        }));

      await createUserAsync({
        ...data,
        companies: assignedCompanies,
      });
      resetForm();
    }

    onClose();
  };

  const resetForm = (): void => {
    setValue("firstName", "");
    setValue("lastName", "");
    setValue("email", "");
    setValue("phoneNumber", "");
    setValue("companies", getCompanyAssignmentFieldArray(true));
  };

  useEffect(() => {
    const companiesAssignment = getCompanyAssignmentFieldArray();

    if (user) {
      setValue("firstName", user.firstName);
      setValue("lastName", user.lastName);
      setValue("email", user.email);
      setValue("phoneNumber", user.phoneNumber);
      setValue("companies", companiesAssignment);
    } else {
      resetForm();
    }
    setError(undefined);
  }, [user, companiesBasics]);

  useEffect(() => {
    if (createUserError) {
      const data = (createUserError as AxiosError)?.response?.data as {
        statusCode: number;
        message: string;
      };

      if (isArray(data?.message)) {
        setError(data.message[0]);
      } else {
        setError(data?.message);
      }
    } else {
      setError(undefined);
    }
  }, [createUserError]);

  useEffect(() => {
    if (updateUserError) {
      const data = (updateUserError as AxiosError)?.response?.data as {
        statusCode: number;
        message: string;
      };

      if (isArray(data?.message)) {
        setError(data.message[0]);
      } else {
        setError(data?.message);
      }
    } else {
      setError(undefined);
    }
  }, [updateUserError]);

  return (
    companiesBasicsEnabled &&
    ((!editMode && createUserEnabled) || (editMode && updateUserEnabled)) && (
      <Drawer open={open} anchor="right" onClose={onClose}>
        <Box p={3} width={800}>
          <Stack spacing={1}>
            <FeatherIcon icon="user-plus" />
            <Typography variant="caption">
              {editMode ? t("title.edit") : t("title.add")}
            </Typography>

            <Typography variant="body2" fontWeight={500}>
              {t("usersManagement.title")}
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography gutterBottom>{t("firstName")}</Typography>
                  <TextField
                    placeholder={t("firstName")}
                    helperText={errors["firstName"]?.message as string}
                    error={Boolean(errors["firstName"])}
                    fullWidth
                    variant="filled"
                    autoFocus
                    disabled={isPending}
                    {...register("firstName", { required: true })}
                  />
                </Grid>
                <Grid size={6}>
                  <Typography gutterBottom>{t("settings.lastName")}</Typography>
                  <TextField
                    placeholder={t("settings.lastName")}
                    helperText={errors["lastName"]?.message as string}
                    error={Boolean(errors["lastName"])}
                    fullWidth
                    variant="filled"
                    disabled={isPending}
                    {...register("lastName", { required: true })}
                  />
                </Grid>
                <Grid size={6}>
                  <Typography gutterBottom>{t("settings.emailAddress")}</Typography>
                  <TextField
                    placeholder={t("settings.emailAddress")}
                    helperText={
                      (errors["email"]?.message as string) ||
                      error === "email_exists"
                        ? t("emailAlreadyExists")
                        : ""
                    }
                    error={Boolean(errors["email"]) || error === "email_exists"}
                    fullWidth
                    type="email"
                    variant="filled"
                    disabled={editMode || isPending}
                    {...register("email", { required: true })}
                  />
                </Grid>
                <Grid size={6}>
                  <Typography gutterBottom>{t("settings.phone")}</Typography>
                  <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <MuiTelInput
                        {...field}
                        value={field.value ?? ""}
                        defaultCountry="CZ"
                        placeholder={t("settings.phone")}
                        helperText={
                          (errors["phoneNumber"]?.message as string) ||
                          error === "phone_exists"
                            ? t("phoneNumberAlreadyExists")
                            : error === "phone_number_invalid"
                              ? t("invalidPhoneNumber")
                              : ""
                        }
                        error={
                          Boolean(errors["phoneNumber"]) ||
                          error === "phone_exists" ||
                          error === "phone_number_invalid"
                        }
                        fullWidth
                        variant="filled"
                        disabled={isPending}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <CompanyAssignmentTable
                    control={control}
                    user={user}
                    disabled={isPending}
                  />
                </Grid>
                <Grid size={12} marginTop={2}>
                  <Button
                    type="submit"
                    fullWidth
                    size="large"
                    disabled={
                      isPending ||
                      isEmpty(companies?.filter((company) => company.isAssigned))
                    }
                  >
                    {isPending && <CircularProgress size={24} />}
                    {!isPending && editMode && t("actions.save")}
                    {!isPending && !editMode && t("actions.create")}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Stack>
        </Box>
      </Drawer>
    )
  );
};

export default UserEditor;
